const TS_DEBUG = false;
function tsLog(...args) {
    if (TS_DEBUG) console.log(...args);
}

tsLog('[TS] totalstudio.js loaded');

const observer = new MutationObserver((mutations, obs) => {
    tsLog('[TS] MutationObserver fired, trying init...');
    if (tryInit()) obs.disconnect();
});

// Active Barn2 slot that triggered the dialog
let activeWrapper = null;

// The standalone dialog we build once and reuse
let pickerDialog = null;

// Abort controller for the variant-info fetch
let variantInfoAbortCtrl = null;

/** Ensures submit/cart listeners for Barn2 alignment are only registered once */
let _tsBarn2CartGuardsInstalled = false;

/**
 * How many flavour selects Barn2 exposes in the discounts block right now (1-pack vs multi-pack bundles).
 *
 * @returns {number}
 */
function getBarn2BundleSelectCount() {
    const barn2Discounts = document.querySelector('#barn2_discounts_block');
    if (!barn2Discounts) return 0;
    return barn2Discounts.querySelectorAll('.barn2-dbs-variant select').length;
}

/** One-shot cache: PDP option1-style label how the shopper first landed (never updated when they pick another íz later). */
let _tsLandingBundleOptionLabel = /** @type {string | null} */ (null);

/** Slot count after the last picker rebuild; `-1` = not yet tracked. Bundle tier jumps reset every slot to the landing íz. */
let _tsLastBarn2SlotCount = -1;

/**
 * @param {any} v
 */
function optionLabelFromB2VariantRecord(v) {
    if (!v) return '';
    const o1 = v.option1 ? String(v.option1).trim() : '';
    if (o1) return o1;
    const t = v.title ? String(v.title).trim() : '';
    return t.includes(' / ') ? t.split(' / ')[0].trim() : t;
}

/**
 * First paint only: íz label from ?variant=, variant-picker JSON, checked radio, or first variant — not the live picker state after user changes íz.
 *
 * @param {Element | null | undefined} variantPicker
 * @returns {string}
 */
function getLandingBundleOptionLabel(variantPicker) {
    if (_tsLandingBundleOptionLabel !== null) return _tsLandingBundleOptionLabel;

    const url = new URL(window.location.href);
    const vidFromUrlRaw = url.searchParams.get('variant');
    const vidFromUrl = vidFromUrlRaw ? String(vidFromUrlRaw).trim() : '';

    /** @type {any} */
    const b2 = /** @type {any} */ (window).b2ProductData;
    const variants = b2?.product?.variants;

    let label = '';

    if (vidFromUrl && Array.isArray(variants)) {
        const vr = variants.find(x => String(x?.id) === vidFromUrl);
        label = optionLabelFromB2VariantRecord(vr);
    }

    if (!label && variantPicker instanceof Element) {
        const slot = variantPicker.querySelector('script[type="application/json"]')?.textContent;
        if (slot) {
            try {
                const parsed = JSON.parse(slot);
                if (parsed && typeof parsed === 'object') {
                    if (!vidFromUrl || String(parsed.id) === vidFromUrl) {
                        label = optionLabelFromB2VariantRecord(parsed);
                    }
                }
            } catch (e) {
                /* ignore */
            }
        }
    }

    if (!label && variantPicker instanceof Element) {
        const checked = variantPicker.querySelector('fieldset input[type="radio"]:checked');
        if (checked instanceof HTMLInputElement && checked.value) label = String(checked.value).trim();
    }

    if (!label && Array.isArray(variants) && variants[0]) {
        label = optionLabelFromB2VariantRecord(variants[0]);
    }

    _tsLandingBundleOptionLabel = label;
    return label;
}

/**
 * When bundle tier changes (1↔3↔6…), every Barn2 row should start from the landing íz, not the last single-pack pick.
 *
 * @param {Element} container
 */
function applyLandingOptionToAllBarn2Selects(container) {
    const vp = document.querySelector('variant-picker[data-template-product-match="true"]');
    const landing = getLandingBundleOptionLabel(vp);
    if (!landing || !(container instanceof Element)) return;

    container.querySelectorAll('.barn2-dbs-variant select').forEach((sel) => {
        if (!(sel instanceof HTMLSelectElement)) return;
        const has = Array.from(sel.options).some((o) => o.value === landing);
        if (!has) return;
        if (sel.value === landing) return;
        sel.value = landing;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

/**
 * Sync Shopify product-form hidden variant id — only meaningful for single-line Barn2 bundles
 * where the PDP form drives add-to-cart. Multi-slot bundles manage their own lines; forcing
 * this input from URL was adding a wrong duplicate line item.
 *
 * @param {string} variantIdStr
 */
function syncMainProductFormVariantId(variantIdStr, options = undefined) {
    const disabled = !!(options && options.disabled === true);
    document.querySelectorAll('form.shopify-product-form input[name="id"], product-form-component input[name="id"]').forEach((el) => {
        if (!(el instanceof HTMLInputElement)) return;
        el.disabled = disabled;
        if (!disabled && variantIdStr) el.value = String(variantIdStr);
    });
}

/**
 * Resolve Shopify variant id from flavour label (Barn2 select value / option1).
 *
 * @param {string} optionValue
 * @returns {string}
 */
function resolveVariantIdFromOptionLabel(optionValue) {
    if (!optionValue) return '';
    try {
        /** @type {any} */
        const b2 = /** @type {any} */ (window).b2ProductData;
        const variants = b2?.product?.variants;
        if (!Array.isArray(variants)) return '';
        const v = variants.find(
            /** @param {any} vv */ (vv) => vv?.title === optionValue || vv?.option1 === optionValue
        );
        return v?.id ? String(v.id) : '';
    } catch (e) {
        return '';
    }
}

/**
 * Barn2 multi-slot bundles still require a valid enabled `input[name="id"]` on the product form
 * (Shopify rejects cart/add when `id` is missing). Use the first bundle slot's flavour as that id.
 */
function syncMainProductFormVariantIdFromFirstBarn2Slot() {
    const barn2 = document.querySelector('#barn2_discounts_block');
    if (!barn2) return;
    const first = barn2.querySelector('.barn2-dbs-variant select');
    if (!(first instanceof HTMLSelectElement)) return;
    let label = String(first.value || '').trim();
    if (!label) label = resolveBarn2SelectDisplayLabel(first, '');
    const vid = resolveVariantIdFromOptionLabel(String(label));
    if (vid) syncMainProductFormVariantId(vid, { disabled: false });
}

/**
 * Keep custom triggers + hidden `input[name="id"]` aligned with native Barn2 `<select>` values.
 * Stale landing-page / previous-add variant ids (e.g. Mango) were intermittently submitted with the wrong line.
 */
function realignBarn2UiAndFormVariantId() {
    const barn2 = document.querySelector('#barn2_discounts_block');
    if (!barn2) return;

    const selects = Array.from(barn2.querySelectorAll('.barn2-dbs-variant select'));

    selects.forEach((sel) => {
        if (!(sel instanceof HTMLSelectElement)) return;
        const raw = String(sel.value || '').trim();
        const wrap = sel.previousElementSibling;
        if (
            raw
            && wrap
            && /** @type {HTMLElement} */ (wrap).classList.contains('ts-variant-wrapper')
            && typeof /** @type {any} */ (wrap)._setSelected === 'function'
        ) {
            /** @type {any} */ (wrap)._setSelected(raw);
        }
    });

    const n = selects.length;
    if (n === 0) return;
    if (n === 1) {
        const sel = /** @type {HTMLSelectElement} */ (selects[0]);
        const vid = resolveVariantIdFromOptionLabel(String(sel.value || '').trim());
        if (vid) syncMainProductFormVariantId(vid, { disabled: false });
    } else {
        syncMainProductFormVariantIdFromFirstBarn2Slot();
    }
}

/** @type {boolean} */
let _tsCartRealignRafScheduled = false;
const TS_CART_UPDATE_EVENT = 'cart:update';

/**
 * After cart mutations, Barn2/theme can leave hidden variant id desynced from visible bundle picks — realign deferred.
 */
function scheduleRealignBarn2AfterCart() {
    if (_tsCartRealignRafScheduled) return;
    _tsCartRealignRafScheduled = true;
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            _tsCartRealignRafScheduled = false;
            realignBarn2UiAndFormVariantId();
        });
    });
}

function installBarn2SubmitAndCartGuards() {
    if (_tsBarn2CartGuardsInstalled) return;
    _tsBarn2CartGuardsInstalled = true;

    document.addEventListener(
        'submit',
        (e) => {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;
            if (!form.classList.contains('shopify-product-form')) return;
            if (!form.closest('product-form-component')) return;
            if (!document.querySelector('#barn2_discounts_block')) return;
            realignBarn2UiAndFormVariantId();
        },
        true
    );

    document.addEventListener(TS_CART_UPDATE_EVENT, (e) => {
        if (/** @type {any} */ (e).detail?.data?.didError) return;
        if (!document.querySelector('#barn2_discounts_block')) return;
        scheduleRealignBarn2AfterCart();
    });
}

/**
 * Robust label for `<select>` when Barn2 sometimes leaves `.value` empty after re-render (custom trigger would show blank).
 *
 * Never reads `?variant=` implicitly: while Barn2 mounts rows, `getBarn2BundleSelectCount()` can briefly be 1, which
 * wrongly forced the landing-page flavour onto unrelated slots (e.g. middle pick shows Mango on a 3-pack).
 * URL-based flavour is applied only when callers pass `fallbackFromSyncedUrl` (e.g. syncInitialVariantFromUrl).
 *
 * @param {HTMLSelectElement} select
 * @param {string} [fallbackFromSyncedUrl] From syncInitialVariantFromUrl (`valForSelect`) when we intentionally match PDP.
 * @returns {string}
 */
function resolveBarn2SelectDisplayLabel(select, fallbackFromSyncedUrl = '') {
    if (!(select instanceof HTMLSelectElement)) return '';

    let v = String(select.value || '').trim();
    if (v) return v;

    const urlFb = fallbackFromSyncedUrl;
    if (
        urlFb
        && Array.from(select.options).some((/** @type {HTMLOptionElement} */ o) => o.value === urlFb)
    ) {
        select.value = urlFb;
        return urlFb;
    }

    const ix = select.selectedIndex;
    if (ix >= 0 && select.options[ix]) {
        const fromIx = String(select.options[ix].value || '').trim();
        if (fromIx) return fromIx;
    }

    const first = select.options[0];
    return first ? String(first.value || '').trim() : '';
}

/**
 * Activates the correct product gallery image for the given option value.
 *
 * @param {string} optionValue    - The selected option label (e.g. "Málna")
 * @param {string} _productId     - The product ID string (reserved for future use)
 * @param {HTMLElement} variantPicker - The variant-picker element (provides sectionId)
 */
function updateProductMedia(optionValue, _productId, variantPicker) {
    /** @type {any} */
    const b2 = /** @type {any} */ (window).b2ProductData;
    const variants = b2?.product?.variants;
    if (!variants) return;

    const variant = variants.find(/** @param {any} v */ v => v.option1 === optionValue || v.title === optionValue);
    if (!variant?.featured_media) return;

    const sectionId = /** @type {HTMLElement} */ (variantPicker).dataset.sectionId;
    const mediaId = variant.featured_media.id;

    const gallery = /** @type {any} */ (
        document.getElementById(`MediaGallery-${sectionId}`)
        || document.querySelector('media-gallery')
    );
    if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(`${sectionId}-${mediaId}`, true);
    }
}

async function updateVariantInfoBlocks(
    optionValue,
    productUrl,
    productId,
    variantMap,
    variantIdOverride = ''
) {
    const anyPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!anyPicker || !productUrl) return;

    let variantId = String(variantIdOverride || '');
    if (!variantId) {
        const esc = CSS.escape ? CSS.escape(optionValue) : optionValue.replace(/"/g, '\\"');
        const radio = anyPicker.querySelector(`input[type="radio"][value="${esc}"]`);
        variantId = /** @type {HTMLElement} */ (radio)?.dataset.variantId || '';
    }
    if (!variantId) return;

    // Update product gallery image immediately (no fetch needed)
    updateProductMedia(optionValue, productId, /** @type {HTMLElement} */ (anyPicker));

    // Abort previous in-flight request
    variantInfoAbortCtrl?.abort();
    variantInfoAbortCtrl = new AbortController();

    try {
        // Shopify Section Rendering API: only fetches the ts_variant-info section HTML
        const resp = await fetch(`${productUrl}?variant=${variantId}&section_id=ts_variant-info`, {
            signal: variantInfoAbortCtrl.signal,
        });
        if (!resp.ok) return;
        const html = new DOMParser().parseFromString(await resp.text(), 'text/html');

        /**
         * IMPORTANT:
         * Only replace the specific variant blocks, never a broad wrapper container.
         * Some product pages contain app widgets (e.g. MBC bundles) inside the same
         * layout area; replacing a parent container can temporarily remove/hide them.
         */
        const targets = [
            {
                name: 'variant-info',
                scopedSelector: `variant-info[data-product-id="${productId}"]`,
                fallbackSelector: 'variant-info',
            },
            {
                name: 'variant-nutritional',
                scopedSelector: `variant-nutritional[data-product-id="${productId}"]`,
                fallbackSelector: 'variant-nutritional',
            },
        ];

        // Debug info + make sure we update ALL matching blocks (not only the first match)
        console.log('[TS] updateVariantInfoBlocks', {
            optionValue,
            variantId,
            productId,
            productUrl,
        });

        targets.forEach(({ name, scopedSelector, fallbackSelector }) => {
            const liveEls = Array.from(document.querySelectorAll(scopedSelector));
            const nextEls = Array.from(html.querySelectorAll(scopedSelector));

            // Fallback to unscoped selectors if scoped didn't find anything
            const liveList = liveEls.length ? liveEls : Array.from(document.querySelectorAll(fallbackSelector));
            const nextList = nextEls.length ? nextEls : Array.from(html.querySelectorAll(fallbackSelector));

            console.log('[TS] block match counts', {
                name,
                live: liveList.length,
                next: nextList.length,
                scopedSelector,
            });

            if (!liveList.length) return;

            // If the next HTML doesn't contain the block, clear/hide all live blocks so we don't show stale data
            if (!nextList.length) {
                liveList.forEach((liveEl) => {
                    liveEl.innerHTML = '';
                    liveEl.setAttribute('hidden', 'hidden');
                });
                return;
            }

            // Replace each live block with a corresponding next block (clone so we can reuse when counts differ)
            liveList.forEach((liveEl, idx) => {
                const nextEl = nextList[idx] || nextList[0];
                const replacement = /** @type {Element} */ (nextEl.cloneNode(true));
                liveEl.replaceWith(replacement);
            });
        });

        // Only sync the theme variant-picker when there is a single Barn2 flavour line.
        // Multi-slot bundles: each slot has its own select; syncing the whole picker to every
        // slot change confuses state and can interfere with add-to-cart.
        if (getBarn2BundleSelectCount() <= 1) {
            document.querySelectorAll(`variant-picker[data-template-product-match="true"][data-product-id="${productId}"]`).forEach(vp => {
                // Radios
                vp.querySelectorAll('input[type="radio"]').forEach(r => {
                    const match = r.value === optionValue;
                    r.checked = match;
                    r.dataset.currentChecked = match ? 'true' : 'false';
                    r.dataset.previousChecked = 'false';
                });

                // Drawer list selected state
                vp.querySelectorAll('.variant-picker-drawer__item').forEach(btn => {
                    const label = btn.querySelector('.variant-picker-drawer__item-label')?.textContent.trim();
                    btn.classList.toggle('variant-picker-drawer__item--selected', label === optionValue);
                });

                // Drawer trigger label
                const triggerLabel = vp.querySelector('[data-variant-picker-drawer-trigger-label]');
                if (triggerLabel) triggerLabel.textContent = optionValue;

                // Drawer trigger icon — copy from drawer list item
                const matchedItem = Array.from(
                    vp.querySelectorAll('.variant-picker-drawer__item')
                ).find(btn => btn.querySelector('.variant-picker-drawer__item-label')?.textContent.trim() === optionValue);

                if (matchedItem) {
                    const triggerIcon = vp.querySelector('.variant-picker-drawer__trigger-icon');
                    const newIcon = matchedItem.querySelector('.variant-picker-drawer__item-icon');
                    if (triggerIcon && newIcon) triggerIcon.innerHTML = newIcon.innerHTML;
                }

                // Trigger aria-label
                const drawerTrigger = vp.querySelector('[data-variant-picker-drawer-trigger]');
                if (drawerTrigger) drawerTrigger.setAttribute('aria-label', `Változat: ${optionValue}`);
            });
        }

    } catch (e) {
        if (e.name !== 'AbortError') console.error('[totalstudio] variant-info update failed:', e);
    }
}

function buildVariantMap(variantPicker) {
    const map = {};
    variantPicker.querySelectorAll('.variant-picker-drawer__item').forEach(item => {
        const label = item.querySelector('.variant-picker-drawer__item-label')?.textContent.trim();
        const iconEl = item.querySelector('.variant-picker-drawer__item-icon');
        if (label) {
            map[label] = { iconHTML: iconEl ? iconEl.innerHTML : '' };
        }
    });
    return map;
}

function renderTrigger(trigger, value, variantMap) {
    const iconData = variantMap[value] || {};
    trigger.innerHTML = `
        <span class="variant-picker-drawer__trigger-icon">${iconData.iconHTML || ''}</span>
        <span class="variant-picker-drawer__trigger-label">${value}</span>
        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
            <path d="M0.75 1.25h8.5L5 5.75 0.75 1.25z" fill="currentColor"></path>
        </svg>
    `;
    trigger.setAttribute('aria-label', `Változat: ${value}`);
}

function openDialog() {
    if (!pickerDialog || pickerDialog.open) return;
    const scrollY = window.scrollY;
    pickerDialog._scrollY = scrollY;

    requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        pickerDialog.showModal();
    });
}

async function closeDialog() {
    if (!pickerDialog || !pickerDialog.open) return;

    // Mirror the theme's close animation
    pickerDialog.style.animation = 'none';
    void pickerDialog.offsetWidth;
    pickerDialog.classList.add('dialog-closing');
    pickerDialog.style.animation = '';

    await new Promise(resolve => {
        pickerDialog.addEventListener('animationend', resolve, { once: true });
        // Fallback if animation doesn't fire
        setTimeout(resolve, 400);
    });

    const scrollY = pickerDialog._scrollY || 0;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    pickerDialog.close();
    pickerDialog.classList.remove('dialog-closing');
}

// Product context set once in onBarn2Ready
let _productUrl = '';
let _productId = '';
let _variantMap = {};

function buildDialog(options, variantMap) {
    const dialog = document.createElement('dialog');
    dialog.className = 'variant-picker-drawer__dialog dialog-modal dialog-drawer dialog-drawer--right';
    dialog.setAttribute('aria-labelledby', 'ts-drawer-heading');

    const inner = document.createElement('div');
    inner.className = 'variant-picker-drawer__inner';

    // Header
    const header = document.createElement('div');
    header.className = 'variant-picker-drawer__header';
    header.innerHTML = `
        <h2 id="ts-drawer-heading" class="variant-picker-drawer__heading h4">Válasszon ízt</h2>
        <button type="button" class="button close-button variant-picker-drawer__close button-unstyled" aria-label="Párbeszédablak bezárása">
            <span class="svg-wrapper">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </span>
        </button>
    `;

    // Variant list
    const ul = document.createElement('ul');
    ul.className = 'variant-picker-drawer__list list-unstyled';

    options.forEach(optionValue => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'variant-picker-drawer__item';
        btn.dataset.value = optionValue;

        const iconData = variantMap[optionValue] || {};
        btn.innerHTML = `
            <span class="variant-picker-drawer__item-icon">${iconData.iconHTML || ''}</span>
            <span class="variant-picker-drawer__item-label">${optionValue}</span>
        `;

        btn.addEventListener('click', async () => {
            if (!activeWrapper) return;

            const val = optionValue;
            activeWrapper._select.value = val;
            activeWrapper._select.dispatchEvent(new Event('change', { bubbles: true }));
            activeWrapper._setSelected(val);

            // Note: the <select> change listener also triggers updateVariantInfoBlocks.
            // Keep this redundant call removed to avoid double fetches.

            // Update selected state in list
            ul.querySelectorAll('.variant-picker-drawer__item').forEach(b => {
                b.classList.toggle('variant-picker-drawer__item--selected', b.dataset.value === val);
            });

            activeWrapper = null;
            await closeDialog();
        });

        li.appendChild(btn);
        ul.appendChild(li);
    });

    inner.appendChild(header);
    inner.appendChild(ul);
    dialog.appendChild(inner);

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
        if (e.target === dialog) closeDialog();
    });

    // Close on Escape key
    dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeDialog();
    });

    // Close button
    header.querySelector('.variant-picker-drawer__close').addEventListener('click', closeDialog);

    document.body.appendChild(dialog);
    return dialog;
}

function markSelected(value) {
    if (!pickerDialog) return;
    pickerDialog.querySelectorAll('.variant-picker-drawer__item').forEach(btn => {
        btn.classList.toggle('variant-picker-drawer__item--selected', btn.dataset.value === value);
    });
}

function createCustomPicker(select, variantMap) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ts-variant-wrapper';

    const trigger = document.createElement('div');
    trigger.className = 'ts-variant-trigger variant-picker-drawer__trigger variant-option__select-wrapper';
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'dialog');

    const setSelected = (value) => renderTrigger(trigger, value, variantMap);

    trigger.addEventListener('click', () => {
        activeWrapper = wrapper;
        markSelected(resolveBarn2SelectDisplayLabel(select));
        openDialog();
    });

    trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activeWrapper = wrapper;
            markSelected(resolveBarn2SelectDisplayLabel(select));
            openDialog();
        }
    });

    setSelected(resolveBarn2SelectDisplayLabel(select));

    // When Barn2 updates the real <select>, keep variant-info / variant-nutritional in sync.
    // Barn2 option values are not guaranteed to match the theme radio values, so derive variantId robustly.
    select.addEventListener('change', () => {
        const selectedOpt = select.options[select.selectedIndex];
        const optionValue = select.value || selectedOpt?.value || '';
        if (!optionValue) return;

        let variantId = selectedOpt?.dataset?.variantId || '';

        // Sometimes the select value itself is the variant id
        if (!variantId && /^\d+$/.test(optionValue)) {
            variantId = optionValue;
        }

        // Fallback: Barn2 global product data
        if (!variantId) {
            try {
                /** @type {any} */
                const b2 = /** @type {any} */ (window).b2ProductData;
                const variants = b2?.product?.variants;
                if (Array.isArray(variants)) {
                    const v = variants.find(vv => vv?.title === optionValue || vv?.option1 === optionValue);
                    if (v?.id) variantId = String(v.id);
                }
            } catch (e) {
                // ignore
            }
        }

        // Update variant-info / variant-nutritional without morphing the whole section
        updateVariantInfoBlocks(optionValue, _productUrl, _productId, _variantMap, variantId);

        const nSlots = getBarn2BundleSelectCount();
        if (nSlots === 1 && variantId) {
            syncMainProductFormVariantId(variantId, { disabled: false });
        } else if (nSlots > 1) {
            // Keep form `id` valid for Shopify; anchor to first slot so we never submit a disabled/missing id.
            syncMainProductFormVariantIdFromFirstBarn2Slot();
        }
    });

    wrapper.appendChild(trigger);
    wrapper._setSelected = setSelected;
    wrapper._select = select;

    return wrapper;
}

/**
 * Remove custom PDP pickers and restore native selects so tier changes (e.g. 3 → 6 slots)
 * cannot leave stale `.ts-variant-wrapper` UI bound to refreshed `<select>` values.
 *
 * @param {Element | null | undefined} container
 */
function stripBarn2CustomPickers(container) {
    if (!(container instanceof Element)) return;
    container.querySelectorAll('.ts-variant-wrapper').forEach((w) => {
        if (typeof w.remove === 'function') w.remove();
    });
    container.querySelectorAll('.barn2-dbs-variant select').forEach((sel) => {
        sel.removeAttribute('data-ts-replaced');
        if (sel instanceof HTMLElement) sel.style.display = '';
    });
}

function replaceSelects(container, variantMap) {
    const allSelects = container.querySelectorAll('.barn2-dbs-variant select');
    const unprocessed = container.querySelectorAll('.barn2-dbs-variant select:not([data-ts-replaced])');
    const wrapCount = container.querySelectorAll('.ts-variant-wrapper').length;
    tsLog(`[TS] replaceSelects: found ${allSelects.length} total selects, ${unprocessed.length} unprocessed`);

    const newCount = allSelects.length;

    // Barn2 removed selects (tier down) — drop stray wrappers
    if (!newCount) {
        stripBarn2CustomPickers(container);
        _tsLastBarn2SlotCount = 0;
        return;
    }

    // New naked selects appear when slots are added (3→6). Also heal when wrapper count drifted (orphans).
    const needsFreshPickers =
        unprocessed.length > 0 ||
        wrapCount !== newCount;
    if (!needsFreshPickers) return;

    const tierSlotCountChanged =
        _tsLastBarn2SlotCount >= 0 && newCount > 0 && newCount !== _tsLastBarn2SlotCount;

    stripBarn2CustomPickers(container);

    if (tierSlotCountChanged) {
        applyLandingOptionToAllBarn2Selects(container);
    }

    const toWrap = container.querySelectorAll('.barn2-dbs-variant select');
    toWrap.forEach((select) => {
        if (!(select instanceof HTMLSelectElement)) return;
        tsLog('[TS] replaceSelects: replacing select', select);
        select.setAttribute('data-ts-replaced', '1');
        select.style.display = 'none';

        const picker = createCustomPicker(select, variantMap);
        select.parentElement?.insertBefore(picker, select);
    });

    _tsLastBarn2SlotCount = container.querySelectorAll('.barn2-dbs-variant select').length;
}

function onBarn2Ready(block, discounts) {
    tsLog('[TS] onBarn2Ready() called');

    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) {
        tsLog('[TS] onBarn2Ready: variant-picker[data-template-product-match="true"] NOT found — aborting');
        return;
    }
    tsLog('[TS] onBarn2Ready: variantPicker found', variantPicker);

    // Lock "arrival íz" before any tier / Barn2 mutations so multi-pack rows never inherit a later single-pack pick.
    getLandingBundleOptionLabel(variantPicker);

    const variantMap = buildVariantMap(variantPicker);
    tsLog('[TS] onBarn2Ready: variantMap built', variantMap);

    // Store product context for the fetch-based variant-info updater
    _productUrl = variantPicker.dataset.productUrl || '';
    _productId  = variantPicker.dataset.productId  || '';
    _variantMap = variantMap;
    tsLog('[TS] onBarn2Ready: productUrl=', _productUrl, 'productId=', _productId);

    // Get option values: prefer DOM select, fallback to Barn2's global product data
    let options = [];
    const firstSelect = discounts.querySelector('.barn2-dbs-variant select');
    if (firstSelect) {
        options = Array.from(firstSelect.options).map(o => o.value);
        tsLog('[TS] onBarn2Ready: options from DOM select', options);
    } else if (window.b2ProductData?.product?.variants) {
        options = window.b2ProductData.product.variants.map(v => v.title);
        tsLog('[TS] onBarn2Ready: options from b2ProductData', options);
    } else {
        tsLog('[TS] onBarn2Ready: NO options found — no .barn2-dbs-variant select and no b2ProductData');
    }

    if (!options.length) {
        tsLog('[TS] onBarn2Ready: options array is empty — aborting');
        return;
    }

    // Build the standalone dialog once
    if (!pickerDialog) {
        tsLog('[TS] onBarn2Ready: building dialog');
        pickerDialog = buildDialog(options, variantMap);
    } else {
        tsLog('[TS] onBarn2Ready: dialog already exists, reusing');
    }

    tsLog('[TS] onBarn2Ready: calling replaceSelects');
    replaceSelects(discounts, variantMap);
    syncInitialVariantFromUrl();
    if (getBarn2BundleSelectCount() > 1) {
        syncMainProductFormVariantIdFromFirstBarn2Slot();
    }

    // Observe a stable ancestor so the observer survives DOM morphs that replace #barn2_discounts_block
    const stableAncestor = discounts.closest('.group-block-content') || document.body;
    let barn2ObserverScheduled = false;
    const barn2Observer = new MutationObserver(() => {
        if (barn2ObserverScheduled) return;
        barn2ObserverScheduled = true;
        requestAnimationFrame(() => {
            barn2ObserverScheduled = false;
            const freshDiscounts = document.querySelector('#barn2_discounts_block');
            if (!freshDiscounts) return;

            const selectCount =
                freshDiscounts.querySelectorAll('.barn2-dbs-variant select').length;
            const wrapperCount = freshDiscounts.querySelectorAll('.ts-variant-wrapper').length;
            const hasUnprocessed =
                !!freshDiscounts.querySelector('.barn2-dbs-variant select:not([data-ts-replaced])');
            const pickerCountDrift =
                (selectCount > 0 && wrapperCount !== selectCount) ||
                (selectCount === 0 && wrapperCount > 0);

            // New slots (`:not([data-ts-replaced])`), tier shrinking (extra wrappers), or similar.
            if (!hasUnprocessed && !pickerCountDrift) return;

            replaceSelects(freshDiscounts, variantMap);
            syncInitialVariantFromUrl({ skipBarn2SingleSelectFromUrl: true });
        });
    });
    barn2Observer.observe(stableAncestor, { childList: true, subtree: true });
}

function tryInit() {
    tsLog('[TS] tryInit() called');

    const barn2Discounts = document.querySelector('#barn2_discounts_block');
    if (!barn2Discounts) {
        tsLog('[TS] tryInit: #barn2_discounts_block NOT found');
        return false;
    }
    tsLog('[TS] tryInit: #barn2_discounts_block found', barn2Discounts);

    const barn2Block = barn2Discounts.parentElement;
    onBarn2Ready(barn2Block, barn2Discounts);
    return true;
}

/**
 * Attaches a change listener to the theme's variant-picker so the accordion
 * content updates when the user selects a variant via the standard theme UI
 * (pills, radios, dropdowns) — independently of the Barn2 widget.
 */
function initThemePickerListener() {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker || variantPicker.dataset.tsListenerAttached) return;
    variantPicker.dataset.tsListenerAttached = '1';

    const productUrl = variantPicker.dataset.productUrl || '';
    const productId  = variantPicker.dataset.productId  || '';

    variantPicker.addEventListener('change', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);

        // Radio / pill style
        if (target instanceof HTMLInputElement && target.type === 'radio') {
            if (!target.checked) return;
            updateVariantInfoBlocks(target.value, productUrl, productId, _variantMap, target.dataset.variantId || '');
            return;
        }

        // Dropdown style
        if (target instanceof HTMLSelectElement) {
            const selectedOption = target.options[target.selectedIndex];
            if (!selectedOption) return;
            const optionValue = target.value;
            const variantId = selectedOption.dataset.variantId || '';
            updateVariantInfoBlocks(optionValue, productUrl, productId, _variantMap, variantId);
        }
    });

    console.log('[TS] theme variant-picker listener attached');
}

/**
 * On page load, if user lands on a concrete variant (?variant=123),
 * ensure every picker/select reflects that variant:
 * - theme variant-picker radios (all option fieldsets)
 * - Barn2 bundle selects (and our custom triggers)
 * - hidden variant id inputs (input[name="id"])
 *
 * This is a "silent sync": it avoids triggering theme morph/fetch again.
 */
let _tsSyncInitialVariantInProgress = false;
let _tsLastSyncedVariantIdFromUrl = '';

/**
 * @param {{ skipBarn2SingleSelectFromUrl?: boolean }} [options]
 * - skipBarn2SingleSelectFromUrl: when Barn2 re-renders after switching bundle tier (e.g. 6-pack → 1-pack),
 *   do not force the single flavour `<select>` from `?variant=` — the URL often still points at an old
 *   PDP variant (e.g. Málna) and would override Barn2's default for the 1-item offer.
 */
function syncInitialVariantFromUrl(options) {
    const skipBarn2SingleSelectFromUrl = options && options.skipBarn2SingleSelectFromUrl === true;

    if (_tsSyncInitialVariantInProgress) return;
    const url = new URL(window.location.href);
    const variantParam = url.searchParams.get('variant');
    if (!variantParam) return;

    const variantIdStr = String(variantParam);

    _tsSyncInitialVariantInProgress = true;

    /** @type {any | null} */
    let variant = null;

    // Preferred source of truth: the theme already renders the selected variant JSON
    // as `selected_or_first_available_variant`, which respects `?variant=...`.
    const vp = document.querySelector('variant-picker[data-template-product-match="true"]');
    const variantJson = vp?.querySelector('script[type="application/json"]')?.textContent;
    if (variantJson) {
        try {
            const parsed = JSON.parse(variantJson);
            if (parsed && String(parsed.id) === variantIdStr) {
                variant = parsed;
            }
        } catch (e) {
            // ignore
        }
    }

    // Fallback: Barn2 global product data (if present)
    if (!variant) {
        /** @type {any} */
        const b2 = /** @type {any} */ (window).b2ProductData;
        const variants = b2?.product?.variants;
        if (Array.isArray(variants)) {
            variant = variants.find(v => String(v?.id) === variantIdStr) || null;
        }
    }

    if (!variant) {
        _tsSyncInitialVariantInProgress = false;
        return;
    }

    const optionValues = [
        variant.option1 || (variant.title ? String(variant.title).split(' / ')[0] : ''),
        variant.option2 || '',
        variant.option3 || '',
    ].filter(Boolean);

    // 1) Sync ALL theme variant-pickers
    document.querySelectorAll('variant-picker[data-template-product-match="true"]').forEach(vp => {
        // Update radios per option position
        optionValues.forEach((val, index) => {
            const esc = CSS.escape ? CSS.escape(val) : val.replace(/"/g, '\\"');
            const radio = vp.querySelector(
                `fieldset[data-fieldset-index="${index}"] input[type="radio"][value="${esc}"]`
            );
            if (!(radio instanceof HTMLInputElement)) return;

            // Uncheck others in same fieldset to keep data attrs consistent
            const fieldset = radio.closest('fieldset');
            fieldset?.querySelectorAll('input[type="radio"]').forEach(r => {
                if (!(r instanceof HTMLInputElement)) return;
                const match = r === radio;
                r.checked = match;
                r.dataset.currentChecked = match ? 'true' : 'false';
                r.dataset.previousChecked = 'false';
            });
        });

        // Drawer trigger label + list selected state (uses option1 label)
        const label = optionValues[0];
        if (label) {
            const triggerLabel = vp.querySelector('[data-variant-picker-drawer-trigger-label]');
            if (triggerLabel) triggerLabel.textContent = label;

            vp.querySelectorAll('.variant-picker-drawer__item').forEach(btn => {
                const t = btn.querySelector('.variant-picker-drawer__item-label')?.textContent.trim();
                btn.classList.toggle('variant-picker-drawer__item--selected', t === label);
            });

            const drawerTrigger = vp.querySelector('[data-variant-picker-drawer-trigger]');
            if (drawerTrigger) drawerTrigger.setAttribute('aria-label', `Változat: ${label}`);
        }
    });

    const barn2Discounts = document.querySelector('#barn2_discounts_block');
    const selects = barn2Discounts ? Array.from(barn2Discounts.querySelectorAll('.barn2-dbs-variant select')) : [];
    const valForSelect = optionValues[0] || variant.title || '';

    // 2)+3) PDP hidden variant input + Barn2 selects:
    //
    // - Single Barn2 flavour select (e.g. 1-item bundle): align select + hidden input + trigger with URL
    //   so kosár matches ?variant=
    //
    // - Multiple selects (e.g. 3- or 6-pack): NEVER force all selects to URL flavour — user chose each slot;
    //   never overwrite input[name="id"] from URL (caused phantom Málna line items).
    //
    let barn2Touched = false;
    if (!barn2Discounts || !selects.length) {
        syncMainProductFormVariantId(String(variant.id), { disabled: false });
    } else if (selects.length > 1) {
        // Multi-pack: never disable `id` (Shopify returns "parameter missing or invalid"). Anchor to first slot.
        syncMainProductFormVariantIdFromFirstBarn2Slot();
    } else if (selects.length === 1 && valForSelect) {
        if (skipBarn2SingleSelectFromUrl) {
            // Bundle re-render: never force-select from URL; but if `.value` is empty, fall back so trigger is not blank.
            selects.forEach((select) => {
                if (!(select instanceof HTMLSelectElement)) return;
                const label = resolveBarn2SelectDisplayLabel(select, valForSelect);
                const wrapper = select.previousElementSibling;
                if (
                    wrapper
                    && /** @type {HTMLElement} */ (wrapper).classList.contains('ts-variant-wrapper')
                    && typeof /** @type {any} */ (wrapper)._setSelected === 'function'
                ) {
                    /** @type {any} */ (wrapper)._setSelected(label);
                }
            });
            syncMainProductFormVariantIdFromFirstBarn2Slot();
        } else {
            syncMainProductFormVariantId(undefined, { disabled: false });

            if (_tsLastSyncedVariantIdFromUrl === variantIdStr && selects.length > 0) {
                syncMainProductFormVariantId(String(variant.id), { disabled: false });
                _tsSyncInitialVariantInProgress = false;
                return;
            }

            syncMainProductFormVariantId(String(variant.id), { disabled: false });

            selects.forEach((select) => {
                if (!(select instanceof HTMLSelectElement)) return;
                if (select.dataset.tsSyncedVariantId === variantIdStr) return;

                const hasOption = Array.from(select.options).some(o => o.value === valForSelect);
                if (!hasOption) return;

                select.value = valForSelect;
                select.dataset.tsSyncedVariantId = variantIdStr;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                barn2Touched = true;
            });

            selects.forEach((select) => {
                const wrapper = select.previousElementSibling;
                if (
                    wrapper
                    && /** @type {HTMLElement} */ (wrapper).classList.contains('ts-variant-wrapper')
                    && typeof /** @type {any} */ (wrapper)._setSelected === 'function'
                ) {
                    /** @type {any} */ (wrapper)._setSelected(select.value || valForSelect);
                }
            });
        }
    }

    // Only mark URL sync "done" for single-select bundles (avoid blocking future single-bundle syncs).
    if (!skipBarn2SingleSelectFromUrl && (barn2Touched || (selects.length === 1 && valForSelect))) {
        _tsLastSyncedVariantIdFromUrl = variantIdStr;
    }
    _tsSyncInitialVariantInProgress = false;

    console.log('[TS] syncInitialVariantFromUrl applied', { variantId: variant.id, optionValues });
}

// Try immediately (script is deferred so DOM is ready)
// Fallback: MutationObserver handles cases where Barn2 renders dynamically
console.log('[TS] Starting init...');
if (!tryInit()) {
    console.log('[TS] tryInit failed, setting up MutationObserver on document.body');
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
} else {
    console.log('[TS] tryInit succeeded');
}

initThemePickerListener();

installBarn2SubmitAndCartGuards();

